import Vue from 'vue'
import {
  GEOCODER_MIN_QUERY_LENGTH,
  buildSearchDirectionsParams,
  dedupeLocations
} from 'js/lib/planner/search-location'
import { cancelGeocoderSearches } from 'js/services/api/search-direction/geocoder-ranked-search'
import Core from 'core/js/pages/planner/direction-internal-result/_new-search-directions-form'

const ORIGIN = 'origin'
const DESTINATION = 'destination'

function buildEmptyStableDropdown() {
  return {
    primary: [],
    recent: [],
    secondary: [],
    showRecentMessage: false
  }
}

export default Vue.extend({
  name: 'new-search-directions-form-cust-vl',
  extends: Core,

  data() {
    return {
      stableDropdownByField: {
        origin: buildEmptyStableDropdown(),
        destination: buildEmptyStableDropdown()
      }
    }
  },

  computed: {
    activeStableDropdown() {
      return this.stableDropdownByField[this.activeField] || buildEmptyStableDropdown()
    },

    dropdownPrimaryResults() {
      return this.activeStableDropdown.primary
    },

    dropdownRecentResults() {
      return this.activeStableDropdown.recent
    },

    dropdownSecondaryResults() {
      return this.activeStableDropdown.secondary
    },

    showRecentSearchesMessage() {
      return this.activeStableDropdown.showRecentMessage
    }
  },

  methods: {
    getDirectionField(field = this.activeField) {
      return field === ORIGIN ? 'originDirection' : 'destinationDirection'
    },

    getLoadingField(field = this.activeField) {
      return field === ORIGIN ? 'isLoadingOrigin' : 'isLoadingDestination'
    },

    getRecentSearches(field = this.activeField) {
      return field === ORIGIN ? this.recentSearchesOrigin : this.recentSearchesDestination
    },

    getSearchTerm(field = this.activeField) {
      return this[this.getDirectionField(field)] || ''
    },

    resetActiveResults() {
      this.locations = []
      this.filteredCustomStops = []
      this.pointsOfInterest = []
    },

    stopLoading() {
      this.isLoadingOrigin = false
      this.isLoadingDestination = false
    },

    updateStableDropdown(field = this.activeField) {
      const recentSearches = this.getRecentSearches(field)
      const hasSearchTerm = this.getSearchTerm(field).trim().length > 0

      const nextState = {
        primary: [...this.locations],
        recent: hasSearchTerm ? [...recentSearches] : [],
        secondary: [
          ...this.filteredCustomStops,
          ...this.pointsOfInterest
        ],
        showRecentMessage: !hasSearchTerm && recentSearches.length === 0
      }

      this.$set(this.stableDropdownByField, field, nextState)
    },

    clearStableDropdown(field = this.activeField) {
      this.$set(this.stableDropdownByField, field, buildEmptyStableDropdown())
    },

    searchDirections() {
      this.resetPoints()

      const field = this.activeField
      const directionField = this.getDirectionField(field)
      const loadingField = this.getLoadingField(field)
      const searchTerm = this.getSearchTerm(field)
      const normalizedSearchTerm = searchTerm.trim()

      if (!normalizedSearchTerm) {
        cancelGeocoderSearches()
        this[directionField] = ''
        this.resetActiveResults()
        this.stopLoading()
        this.clearStableDropdown(field)
        this.showLocations = true

        return
      }

      // Too short to be worth a Photon request: clear the results instead.
      if (normalizedSearchTerm.length < GEOCODER_MIN_QUERY_LENGTH) {
        cancelGeocoderSearches()
        this.resetActiveResults()
        this.stopLoading()
        this.clearStableDropdown(field)
        this.showLocations = true

        return
      }

      this[loadingField] = true
      this.filteredCustomStops = this.filteredCustomStopsMethod(normalizedSearchTerm, 'stops')
      this.pointsOfInterest = []
      this.updateStableDropdown(field)

      const { municipalityBounds } = this

      const municipalityGeo = this.currentMunicipality ? this.currentMunicipality.geo : null
      const paramsBuscar = buildSearchDirectionsParams(
        normalizedSearchTerm,
        this.currentAppConfig.planner_router_url,
        municipalityBounds,
        20,
        municipalityGeo ? municipalityGeo.lat : null,
        municipalityGeo ? municipalityGeo.lng : null,
        this.userPosition
      )

      const myInterceptor = []

      Vue.http.interceptors.forEach((interceptor, index) => {
        if (typeof interceptor === 'function') {
          myInterceptor.push(interceptor)
          Vue.http.interceptors.splice(index, 1)
        }
      })

      this.buscarpuntosInteres(paramsBuscar, true, { skipLoading: true }).then(response => {
        if ((this[directionField] || '').trim() !== normalizedSearchTerm || this.activeField !== field) return

        try {
          this.pointsOfInterest = this.extractValueAndName(response)
          this.pointsOfInterest = dedupeLocations(this.pointsOfInterest, this.locations)
        } catch {}

        this.updateStableDropdown(field)
      }).catch(() => {
        if ((this[directionField] || '').trim() !== normalizedSearchTerm || this.activeField !== field) return

        this.pointsOfInterest = []
        this.updateStableDropdown(field)
      })

      this.buscar(paramsBuscar, true, { skipLoading: true }).then(response => {
        if ((this[directionField] || '').trim() !== normalizedSearchTerm || this.activeField !== field) return

        this.stopLoading()
        this.locations = this.processResponse(response)
        this.pointsOfInterest = dedupeLocations(this.pointsOfInterest, this.locations)
        this.updateStableDropdown(field)
        this.showLocations = true
      }).catch(() => {
        if ((this[directionField] || '').trim() !== normalizedSearchTerm || this.activeField !== field) return

        this.stopLoading()
        this.locations = []
        this.updateStableDropdown(field)
      })

      Vue.http.interceptors.push(...myInterceptor)
    },

    handleFocus(directionField) {
      this.activeField = directionField
      const searchTerm = this.getSearchTerm(directionField)

      if (searchTerm.trim() === '') {
        this.resetActiveResults()
        this.clearStableDropdown(directionField)
      } else {
        this.updateStableDropdown(directionField)
      }

      this.isSelecting = false
      this.showLocations = true
      this.resetPoints()
    },

    selectLocation(location) {
      Core.options.methods.selectLocation.call(this, location)
      this.clearStableDropdown(this.activeField)
    },

    closeLocations() {
      this.showLocations = false
    },

    closeOriginLocations() {
      if (this.activeField === ORIGIN) {
        this.closeLocations()
      }
    },

    closeDestinationLocations() {
      if (this.activeField === DESTINATION) {
        this.closeLocations()
      }
    }
  }
})
